// Components
import { Grid, Stack, StackProps, Theme, Typography } from "@mui/material";

// Utils & Helpers
import { prefixClassName } from "@smartrr/shared/components/primitives";
import { SMARTRR_CUSTOM_ATTRIBUTES_VALUE } from "@smartrr/shared/constants";
import { AquariusBadge } from "@smartrr/shared/customerPortal/components/AquariusBadge";
import {
  AquariusIconButton,
  IconButtonIcons,
} from "@smartrr/shared/customerPortal/components/AquariusIconButton";
import { IPurchaseState } from "@smartrr/shared/entities/PurchaseState";
import { ScheduledDeliveryGroupWithPurchSt } from "@smartrr/shared/entities/PurchaseState/scheduleUtils";
import {
  ISmartrrInfoCustomAttributes,
  isCPSLineItemApartOfBundle,
  isCPSLineItemBundleParent,
} from "@smartrr/shared/utils/bundles";
import { collapseProducts } from "@smartrr/shared/utils/collapseProducts";
import { localize, typedLocalize } from "@smartrr/shared/utils/sharedTranslations/localize";
import { getPurchasableFromPurchasableMap } from "@smartrr/shared/utils/useVariantToPurchasableMap";
import compact from 'lodash/compact';
import truncate from 'lodash/truncate';
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";

import { HiddenProductsNumber } from "@customer-app/account/views/OperationAquarius/components/HiddenProductsNumber";
import { WIDTH_SIZES } from "@customer-app/account/views/OperationAquarius/components/ProductImage";
import { useSmartrrTheme } from "@customer-app/account/views/OperationAquarius/hooks/useSmartrrTheme";
import { getGift, useMobileQuery } from "@customer-app/account/views/OperationAquarius/utils";
import { formatDeliveryPrice } from "@customer-app/account/views/OperationAquarius/utils/Pricing/Delivery";
import { useCustomerPortalVariantToPurchasableMap } from "@customer-app/state/reducers/purchasables";
import { useSellingPlanGroupForSellingPlanId } from "@customer-app/state/reducers/sellingPlanGroups";
import { useSmartrrCustomerSelector } from "@customer-app/state/typedCustomerReduxHooks";

import { useCurrentBundle } from "../../../../../hooks/useCurrentBundle";
import { useGetAllSellingPlanGroups } from "../../../../../hooks/useGetAllSellingPlanGroups";
import { useUnavailableVariantsAndDeliveries } from "../../../../../hooks/useUnavailableVariantsAndDeliveries";
import { OrderItemImage } from "../../../constants";
import { sortAndFilterLineItemsForDisplay } from "../../../V2SubscriptionManagement/libs/utils";
import { ButtonGrid, giftObj, skipObj, ungiftObj, unskipObj } from "../../constants";
import { ValidViews } from "../../OrderOptionsModalInner";

interface IUpcomingOrderCardProps {
  delivery: ScheduledDeliveryGroupWithPurchSt<IPurchaseState>;
  imageSize?: keyof typeof WIDTH_SIZES;
  setView: React.Dispatch<
    React.SetStateAction<{
      view: ValidViews;
      delivery: ScheduledDeliveryGroupWithPurchSt<IPurchaseState> | undefined;
    }>
  >;
  switchToOrderOptions: (
    delivery: ScheduledDeliveryGroupWithPurchSt<IPurchaseState>,
    isSellingPlanDeleted: boolean
  ) => void;
}

interface IOrderCardProps extends StackProps {
  aquariusTheme: Theme;
}

const OrderCard = styled(({ aquariusTheme, ...rest }: IOrderCardProps) => <Stack {...rest} />).attrs(props => ({
  className: prefixClassName("upcoming-order-card"),
}))<IOrderCardProps>`
  padding: 15px 20px;
  border: 1px solid ${props => props.aquariusTheme.modernSecondaryButtonBorderColor};
  border-radius: 25px;
  margin-top: 15px;
`;

export const UpcomingOrderCard = ({
  delivery,
  imageSize,
  setView,
  switchToOrderOptions,
}: IUpcomingOrderCardProps): JSX.Element => {
  const variantToPurchasableMap = useCustomerPortalVariantToPurchasableMap();
  const isMobile = useMobileQuery();
  const theme = useSmartrrTheme();
  const { date, purchaseState } = delivery;
  const skdIdx = delivery.indexFromScheduleStart;
  const [numOfHidden, setNumOfHidden] = useState<number | null>();
  const isSellingPlanDeleted = !useSellingPlanGroupForSellingPlanId(purchaseState.sellingPlanId).length;
  const isPurchaseStatesInitialized = useSmartrrCustomerSelector(state => state.purchaseStates.isInitialized);
  const isSellingPlanGroupsLoading = useSmartrrCustomerSelector(
    state => state.sellingPlanGroups.sellingPlansIsLoading
  );

  const itemsInDelivery = compact(
    delivery.purchaseState.stLineItems.map(st => {
      return !(st.isAddOn || st.deletedAt);
    })
  );

  const allGroups = useGetAllSellingPlanGroups();

  const isError =
    (isSellingPlanDeleted && isPurchaseStatesInitialized && !isSellingPlanGroupsLoading) ||
    itemsInDelivery.length === 0;

  const { bundle } = useCurrentBundle(delivery);
  const { unavailableVariantsAndDeliveries } = useUnavailableVariantsAndDeliveries();
  const gridRef = useRef<HTMLDivElement>(null);
  const buttonGridRef = useRef<HTMLInputElement>(null);
  const buttonOptionsRef = useRef<HTMLDivElement[]>([]);

  const themeConditionalActions = theme.modernThemeSettings?.nextOrderActions;

  const gift = getGift(purchaseState, skdIdx);

  const sortedLineItems = useMemo(() => sortAndFilterLineItemsForDisplay(purchaseState, skdIdx), [purchaseState]);

  const onClick = useCallback(
    (view: ValidViews) => {
      setView({ view, delivery });
    },
    [delivery]
  );

  const buttonOptions: ({ iconName: IconButtonIcons; label: string; view: ValidViews } | undefined)[] = [
    !gift && themeConditionalActions?.skip ? { ...(delivery.isSkipped ? unskipObj : skipObj) } : undefined,
    !delivery.isSkipped && themeConditionalActions?.gift ? { ...(gift ? ungiftObj : giftObj) } : undefined,
  ].filter(ele => {
    return ele !== undefined;
  });

  useEffect(() => {
    buttonOptionsRef.current = buttonOptionsRef.current.slice(0, buttonOptions.length);
  }, [buttonOptions]);

  useEffect(() => {
    !isError && setNumOfHidden(collapseProducts(gridRef, isMobile, imageSize!));
  }, [gridRef, imageSize, isMobile, purchaseState]);

  let variantQty = 0;
  const singleProductName = useMemo(() => {
    const lastLineItem = sortedLineItems[sortedLineItems.length - 1];
    const purchaseable = getPurchasableFromPurchasableMap(variantToPurchasableMap, lastLineItem?.vnt);
    if (purchaseable) {
      return purchaseable.purchasableName;
    }
    return;
  }, []);

  if (itemsInDelivery.length === 0) {
    return <React.Fragment />;
  }

  return (
    <OrderCard aquariusTheme={theme}>
      <Stack direction="row" spacing="7px" alignItems="center">
        <Typography
          variant="h5"
          style={{ textTransform: "none", marginBottom: "0px" }}
          className={prefixClassName("upcoming-order-date")}
        >
          {typedLocalize("utils.dates.toViewDateLong", { date })}
        </Typography>
        {delivery.isSkipped ? <AquariusBadge icon="forwardLoop" label="Skipped" /> : null}
        {gift ? <AquariusBadge icon="gift" label="Gifted" /> : null}
      </Stack>
      <ButtonGrid
        container
        ref={buttonGridRef}
        spacing={0}
        justifyContent="space-around"
        className={prefixClassName("upcoming-order-actions")}
        direction="row"
        isMobile={isMobile}
        sx={{
          padding: "7px 0px",
        }}
      >
        {buttonOptions.map((ele, index) => {
          return (
            <Grid
              item
              key={index}
              ref={el => (buttonOptionsRef.current[index] = el!)}
              id={`modal-action-${ele?.label}`}
            >
              <AquariusIconButton
                iconName={ele?.iconName!}
                size="large"
                label={localize(`customerActions.${ele?.label}`)}
                onClick={() => onClick(ele?.view!)}
              />
            </Grid>
          );
        })}
        {delivery.isSkipped ? null : (
          <Grid id={`modal-action-more-actions`} item>
            <AquariusIconButton
              iconName={"gear"}
              size="large"
              label={typedLocalize("customerActions.moreActions")}
              onClick={() => switchToOrderOptions(delivery, isError)}
            />
          </Grid>
        )}
      </ButtonGrid>
      <hr />
      <Grid ref={gridRef} container spacing={"10px"} marginTop="7px">
        {sortedLineItems.map(item => {
          const purchasable = getPurchasableFromPurchasableMap(variantToPurchasableMap, item.vnt) || undefined;

          if (!purchasable) {
            return <React.Fragment />;
          }

          const isUnavailable =
            unavailableVariantsAndDeliveries.vnts.some(vnt => vnt.id === item.vnt.id) ||
            unavailableVariantsAndDeliveries.purchasables.some(p => p.id === purchasable.id);

          const isBundleParent = isCPSLineItemBundleParent(item);

          if (bundle && isCPSLineItemApartOfBundle(item)) {
            if (isBundleParent && item.customAttributes?.length) {
              let tempQty = 0;
              const customAttributes = item.customAttributes.find(
                ele => ele.key === SMARTRR_CUSTOM_ATTRIBUTES_VALUE
              );

              const smartrrCustomAttributesParsed: ISmartrrInfoCustomAttributes = customAttributes?.value
                ? JSON.parse(customAttributes.value)
                : {};

              for (const parsedItem of smartrrCustomAttributesParsed.items) {
                tempQty += parsedItem.quantity;
              }
              variantQty += tempQty;
              return (
                <Grid item key={item.id} sx={{ width: "max-content" }}>
                  <OrderItemImage
                    purchasableVariant={item.vnt}
                    quantity={item.quantity}
                    disabled={delivery.isSkipped || isUnavailable}
                    size="small"
                  />
                </Grid>
              );
            }
            return <React.Fragment key={item.id} />;
          }

          variantQty += item.quantity;

          return (
            <Grid item key={item.id} sx={{ width: "max-content" }}>
              <OrderItemImage
                purchasableVariant={item.vnt}
                quantity={item.quantity}
                disabled={delivery.isSkipped || isUnavailable}
                size="small"
              />
            </Grid>
          );
        })}
        {numOfHidden ? (
          <HiddenProductsNumber isMobile={isMobile} numOfHidden={numOfHidden} imageSize="small" />
        ) : null}
      </Grid>

      {delivery.isSkipped ? null : (
        <Stack justifyContent="space-between" alignItems="center" direction="row" sx={{ marginTop: "7px" }}>
          {variantQty > 1 ? (
            <Typography variant="caption1">
              {localize("general.itemCount", {
                count: Number(variantQty),
              })}
            </Typography>
          ) : (
            <Typography variant="caption1" className={prefixClassName("upcoming-order-order-count")}>
              {singleProductName
                ? truncate(singleProductName)
                : localize("general.itemCount", {
                    count: 1,
                  })}
            </Typography>
          )}
          <Typography variant="caption1" className={prefixClassName("upcoming-order-total")}>
            {formatDeliveryPrice(delivery, allGroups, {
              includeAddons: true,
              pricingType: "with_discounts",
            })}
          </Typography>
        </Stack>
      )}
    </OrderCard>
  );
};
